'use client'

import { Check } from 'lucide-react'
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
} from '@/components/ui/dropdown-menu'
import {
    useLanguage,
    LanguageFlagIcon,
    SUPPORTED_LANGUAGES,
    type SupportedLocale,
} from '@/lib/language-context'
import { cn } from '@/lib/utils'

type LanguageSwitcherProps = {
    /** Visual variant */
    variant?: 'header' | 'footer'
    className?: string
}

export function LanguageSwitcher({ variant = 'header', className }: LanguageSwitcherProps) {
    const { locale, setLocale } = useLanguage()

    const currentLang = SUPPORTED_LANGUAGES.find((l) => l.code === locale) ?? SUPPORTED_LANGUAGES[0]

    const handleSelect = (code: SupportedLocale) => {
        setLocale(code)
    }

    if (variant === 'footer') {
        return (
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button
                        className={cn(
                            '!flex !items-center !gap-2 !rounded-full !border !border-slate-200 !bg-white !px-3 !py-1 !text-xs',
                            className,
                        )}
                    >
                        <LanguageFlagIcon code={currentLang.code} className="!h-5 !w-5" />
                        <span>{currentLang.label}</span>
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="min-w-[140px] bg-white">
                    {SUPPORTED_LANGUAGES.map((lang) => (
                        <DropdownMenuItem
                            key={lang.code}
                            onClick={() => handleSelect(lang.code)}
                            className="flex items-center justify-between gap-2"
                        >
                            <span className="flex items-center gap-2 !p-2">
                                <LanguageFlagIcon code={lang.code} className="!h-5 !w-5" />
                                <span>{lang.label}</span>
                            </span>
                            <Check
                                className={cn(
                                    'h-4 w-4 text-[#4568DC]',
                                    locale === lang.code ? 'opacity-100' : 'opacity-0',
                                )}
                            />
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
        )
    }

    // Header variant (default) - show only flag, bigger
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    className={cn(
                        'flex items-center justify-center !text-base hover:!opacity-80 transition-opacity',
                        className,
                    )}
                >
                    <LanguageFlagIcon code={currentLang.code} className="!h-5 !w-5" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[140px] bg-white">
                {SUPPORTED_LANGUAGES.map((lang) => (
                    <DropdownMenuItem
                        key={lang.code}
                        onClick={() => handleSelect(lang.code)}
                        className="flex items-center justify-between gap-2"
                    >
                        <span className="flex items-center gap-2 !p-2">
                            <LanguageFlagIcon code={lang.code} className="!h-5 !w-5" />
                            <span>{lang.label}</span>
                        </span>
                        <Check
                            className={cn(
                                'h-4 w-4 text-[#4568DC]',
                                locale === lang.code ? 'opacity-100' : 'opacity-0',
                            )}
                        />
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    )
}
